// ============================================
// Video Controls Component
// Control bar for video calls
// ============================================

import React from 'react';

/**
 * Microphone Icon
 */
const MicrophoneIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
  </svg>
);

/**
 * Microphone Off Icon
 */
const MicrophoneOffIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
  </svg>
);

/**
 * Camera Icon
 */
const VideoCameraIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
);

/**
 * Camera Off Icon
 */
const VideoCameraOffIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3l18 18" />
  </svg>
);

/**
 * Screen Share Icon
 */
const ScreenShareIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

/**
 * Phone Off Icon (End Call)
 */
const PhoneOffIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M3.34 17.66l1.41 1.41c.51.51 1.19.79 1.9.79.71 0 1.39-.28 1.9-.79l2.2-2.2c.51-.51.79-1.19.79-1.9s-.28-1.39-.79-1.9l-.88-.88c.42-.89.99-1.7 1.68-2.39.69-.69 1.5-1.26 2.39-1.68l.88.88c.51.51 1.19.79 1.9.79s1.39-.28 1.9-.79l2.2-2.2c.51-.51.79-1.19.79-1.9s-.28-1.39-.79-1.9l-1.41-1.41C18.01 1.08 16.8.58 15.51.58c-1.29 0-2.5.5-3.41 1.41l-2.2 2.2C8.79 5.3 8.21 6.63 8.21 8.05s.5 2.75 1.41 3.66l.09.09-.09.09c-.91.91-1.41 2.12-1.41 3.41s.5 2.5 1.41 3.41l.09.09-.09.09c-.91.91-2.12 1.41-3.41 1.41s-2.5-.5-3.41-1.41l-1.41-1.41c-.39-.39-.39-1.02 0-1.41.39-.39 1.02-.39 1.41 0l1.41 1.41c.51.51 1.19.79 1.9.79z" />
  </svg>
);

/**
 * Video Controls - control bar with mute, camera, share, leave buttons
 */
const VideoControls = ({
  isAudioEnabled,
  isVideoEnabled,
  isScreenSharing,
  onToggleAudio,
  onToggleVideo,
  onToggleScreenShare,
  onLeave,
  onEnd,
  roomType
}) => {
  return (
    <div className="flex items-center justify-center gap-3 sm:gap-4 p-3 sm:p-4 bg-gray-800/90 backdrop-blur-sm">
      {/* Microphone toggle */}
      <button
        onClick={onToggleAudio}
        className={`
          p-3 sm:p-4 rounded-full transition-all duration-200
          ${isAudioEnabled
            ? 'bg-gray-700 hover:bg-gray-600 text-white'
            : 'bg-red-600 hover:bg-red-500 text-white'
          }
        `}
        title={isAudioEnabled ? 'Mute microphone' : 'Unmute microphone'}
      >
        {isAudioEnabled ? (
          <MicrophoneIcon className="w-5 h-5 sm:w-6 sm:h-6" />
        ) : (
          <MicrophoneOffIcon className="w-5 h-5 sm:w-6 sm:h-6" />
        )}
      </button>

      {/* Camera toggle */}
      <button
        onClick={onToggleVideo}
        className={`
          p-3 sm:p-4 rounded-full transition-all duration-200
          ${isVideoEnabled
            ? 'bg-gray-700 hover:bg-gray-600 text-white'
            : 'bg-red-600 hover:bg-red-500 text-white'
          }
        `}
        title={isVideoEnabled ? 'Turn off camera' : 'Turn on camera'}
      >
        {isVideoEnabled ? (
          <VideoCameraIcon className="w-5 h-5 sm:w-6 sm:h-6" />
        ) : (
          <VideoCameraOffIcon className="w-5 h-5 sm:w-6 sm:h-6" />
        )}
      </button>

      {/* Screen share toggle */}
      <button
        onClick={onToggleScreenShare}
        className={`
          p-3 sm:p-4 rounded-full transition-all duration-200
          ${isScreenSharing
            ? 'bg-blue-600 hover:bg-blue-500 text-white ring-2 ring-blue-400'
            : 'bg-gray-700 hover:bg-gray-600 text-white'
          }
        `}
        title={isScreenSharing ? 'Stop sharing screen' : 'Share screen'}
      >
        <ScreenShareIcon className="w-5 h-5 sm:w-6 sm:h-6" />
      </button>

      {/* Divider */}
      <div className="w-px h-8 bg-gray-600 mx-2" />

      {/* Leave call button */}
      <button
        onClick={onLeave}
        className="p-3 sm:p-4 rounded-full bg-red-600 hover:bg-red-500 text-white transition-all duration-200"
        title="Leave call"
      >
        <svg className="w-5 h-5 sm:w-6 sm:h-6 transform rotate-[135deg]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
        </svg>
      </button>

      {/* End call for all button (only for DM calls, shown to caller) */}
      {onEnd && roomType === 'dm_call' && (
        <button
          onClick={onEnd}
          className="px-4 py-2 rounded-full bg-red-700 hover:bg-red-600 text-white text-sm font-medium transition-all duration-200 flex items-center space-x-2"
          title="End call for everyone"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
          <span className="hidden sm:inline">End Call</span>
        </button>
      )}
    </div>
  );
};

export default VideoControls;
